import { useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import {
  SessionInvalidationReason,
  sessionInvalidatedEventName,
} from "../api/auth";
import { RouteErrorBoundary } from "../components/RouteErrorBoundary";

function SessionInvalidationDialog({
  onAcknowledge,
  reason,
}: {
  onAcknowledge: () => void;
  reason: SessionInvalidationReason;
}) {
  const dialogRef = useRef<HTMLDialogElement | null>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) {
      dialog.showModal();
    }

    return () => {
      if (dialog?.open) dialog.close();
    };
  }, []);

  const message = reason === "signed-in-elsewhere"
    ? "此账号已在其他设备或迹时运动 App 中登录。请重新登录。"
    : "登录状态已过期。请重新登录。";

  return (
    <dialog
      aria-describedby="session-invalidated-message"
      aria-labelledby="session-invalidated-title"
      className="session-invalidated-dialog"
      onCancel={(event) => {
        event.preventDefault();
        onAcknowledge();
      }}
      ref={dialogRef}
      role="alertdialog"
    >
      <h2 id="session-invalidated-title">登录已失效</h2>
      <p id="session-invalidated-message">{message}</p>
      <button autoFocus type="button" onClick={onAcknowledge}>
        好的
      </button>
    </dialog>
  );
}

export function AppShell() {
  const location = useLocation();
  const [sessionInvalidationReason, setSessionInvalidationReason] = useState<SessionInvalidationReason | null>(null);
  const isHome = location.pathname === "/";
  const isCompat = location.pathname.startsWith("/compat");
  const isResult = location.pathname.startsWith("/r/") || location.pathname === "/student-demo";
  const isDashboard = location.pathname.startsWith("/teacher/dashboard") || location.pathname === "/teacher-demo";
  const isStudentEntry = location.pathname.startsWith("/t/");
  const title = isCompat ? "H5 兼容性检查台" : isDashboard ? "老师看板" : "AI教练学生入口";
  const phase = isCompat ? "阶段 1" : isDashboard ? "阶段 8" : "阶段 4";

  useEffect(() => {
    function handleSessionInvalidated(event: Event) {
      const reason = (event as CustomEvent<SessionInvalidationReason>).detail;
      setSessionInvalidationReason((current) => {
        if (current === "signed-in-elsewhere") return current;
        return reason === "signed-in-elsewhere" ? reason : current ?? reason;
      });
    }

    window.addEventListener(sessionInvalidatedEventName, handleSessionInvalidated);
    return () => window.removeEventListener(sessionInvalidatedEventName, handleSessionInvalidated);
  }, []);

  function handleSessionInvalidationAcknowledgement() {
    setSessionInvalidationReason(null);
    if (isResult) {
      window.location.replace(`${window.location.origin}${window.location.pathname}#/`);
      return;
    }
    window.location.reload();
  }

  return (
    <div
      className={`app-shell ${isDashboard ? "teacher-dashboard-shell" : ""} ${isStudentEntry ? "student-entry-shell" : ""} ${
        isHome ? "teacher-home-shell" : ""
      }`}
    >
      {!isHome && !isStudentEntry && !isResult && !isDashboard ? (
        <header className="topbar">
          <div>
            <p className="eyebrow">迹时运动</p>
            <h1>{title}</h1>
          </div>
          <span className="build-badge">{phase}</span>
        </header>
      ) : null}
      <main>
        <RouteErrorBoundary>
          <Outlet />
        </RouteErrorBoundary>
      </main>
      {sessionInvalidationReason ? (
        <SessionInvalidationDialog
          onAcknowledge={handleSessionInvalidationAcknowledgement}
          reason={sessionInvalidationReason}
        />
      ) : null}
    </div>
  );
}
